import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { MathJaxContext } from 'better-react-mathjax';
import AccordionQA from '../components/AccordionQA';
import FlipCardQA from '../components/FlipCardQA';
import CardScrollerQA from '../components/CardScrollerQA';
import TabsQA from '../components/TabsQA';
import VerticalTimelineQA from '../components/VerticalTimelineQA';
import AutoCarouselQA from '../components/AutoCarouselQA';
import StepperQA from '../components/StepperQA';
import { motion } from 'framer-motion';
import ClarifyModalView from './ClarifyModalView'; // or correct path

const mathJaxConfig = {
  loader: { load: ['input/tex', 'output/chtml'] },
  tex: {
    inlineMath: [['$', '$'], ['\\(', '\\)']],
    displayMath: [['\\[', '\\]']]
  }
};



export default function LearningPage() {
  const [outlineName, setOutlineName] = useState('');
  const { outlineId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('accordion');
  const [clarifyModalId, setClarifyModalId] = useState(null);
  const [clarifyModalOpen, setClarifyModalOpen] = useState(false);
  const handleClarifyModal = (id) => {
    console.log('Opening modal for:', id);
    setClarifyModalId(id);
    setClarifyModalOpen(true);
  };


  const fetchOutlineName = async () => {
    const { data, error } = await supabase
      .from('outline_summary')
      .select('outline_name')
      .eq('outline_id', outlineId)
      .single();
    if (!error && data) setOutlineName(data.outline_name);
  };
  
  fetchOutlineName(); // call it after fetchLearningItems

  useEffect(() => {
    if (!outlineId) return;

    const fetchLearningItems = async () => {
      setLoading(true);
      console.log("Running query for learning content with outline_id:", outlineId);
      const { data, error } = await supabase
      .from('qa_learning')
      .select(`
        *,
        image_links:qa_image_links (
          tag,
          image_id,
          qa_images:image_id (
            image_url,
            label, width_percent
          )
        )
      `)
      .eq('outline_id', outlineId)
      .order('order_index');

      if (error) {
        console.error('Error fetching learning content:', error);
      } else {
        console.log('Fetched learning content:', data);
        setItems(data);
      }

          // fetch outline name too
    const { data: nameData, error: nameError } = await supabase
      .from('outline_summary')
      .select('outline_name')
      .eq('outline_id', outlineId)
      .single();
    if (!nameError && nameData) setOutlineName(nameData.outline_name);
      setLoading(false);
    };

    fetchLearningItems();
  }, [outlineId]);

  const renderThemeComponent = () => {
    const themeProps = {
      items,
      onModalLinkClick: handleClarifyModal
    };
  
    switch (theme) {
      case 'accordion':
        return <AccordionQA {...themeProps} />;
      case 'flipcard':
        return <FlipCardQA {...themeProps} />;
      case 'cardscroller':
        return <CardScrollerQA {...themeProps} />;
      case 'tabs':
        return <TabsQA {...themeProps} />;
      case 'timeline':
        return <VerticalTimelineQA {...themeProps} withAnimations={true} />;
      case 'carousel':
        return <AutoCarouselQA {...themeProps} />;
      case 'stepper':
        return <StepperQA {...themeProps} />;
      default:
        return <AccordionQA {...themeProps} />;
    }
  };

  
  if (loading) return <div className="text-center mt-10 text-gray-500">Loading learning content...</div>;
  if (items.length === 0) return <div className="text-center mt-10 text-gray-500">No learning content found.</div>;

  return (
    <MathJaxContext config={mathJaxConfig}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-5xl mx-auto px-4 py-8"
      >
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => navigate(-1)}
            className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 font-medium px-4 py-2 rounded shadow-sm transition"
          >
            🔙 Back to Subtopic
          </button>
        </div>
        <h4 className="text-xl sm:text-2xl font-bold mb-1 text-gray-800 text-center">
  📘        {outlineName || 'Loading topic...'}
        </h4>
        

        <div className="mb-6 text-center">
          <label className="block font-medium text-gray-600 mb-2">Select Theme:</label>
          <select
            className="px-4 py-2 border border-gray-300 rounded text-sm"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
          >
            <option value="accordion">Accordion</option>
            <option value="flipcard">Flip Card</option>
            <option value="cardscroller">Card Scroller</option>
            <option value="tabs">Tabs</option>
            <option value="timeline">Vertical Timeline</option>
            <option value="carousel">Auto Carousel</option>
            <option value="stepper">Stepper</option>
          </select>
        </div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          {renderThemeComponent()}
        </motion.div>
      </motion.div>
      <ClarifyModalView
          clarifyId={clarifyModalId}
          isOpen={clarifyModalOpen}
          onClose={() => setClarifyModalOpen(false)}
        />
    </MathJaxContext>
  );
}
